import { Directive, Input, OnDestroy, OnInit, TemplateRef, ViewContainerRef } from '@angular/core'
import { Subject, distinctUntilChanged, filter, takeUntil } from 'rxjs'
import { isNotNullOrUndefined } from '../../lib/util'
import { ScreenSize, ScreenSizeService } from '../../services/screen-size/screen-size.service'
import { assertUnreachable } from '../../util'

/**
 * IMPORTANT: These Size Targets replicate exactly what the media queries in media.css do.
 *
 * This is contrary to what ScreenSize Service reports.
 *
 * You can think of the ScreenSize Service as "Up to X Breakpoint" and the SizeTarget as "From X Breakpoint"
 *
 * For example:
 *
 * Small Breakpoint: 640
 * Medium Breakpoint = 1024
 *
 * ScreenSize.Medium = 640 => 1023px (between small and medium)
 * '--medium-only' = 640 => 1023px (between small and medium)
 *
 * We really should make these consistent, but it would require quite a refactor.
 */
export type SizeTarget =
  | 'xsmall-down'
  | 'small-down'
  | 'medium'
  | 'medium-down'
  | 'medium-up'
  | 'large'
  | 'large-up'
  | 'xlarge'

const SmallDown = new Set([ScreenSize.XSmall, ScreenSize.Small])
const MediumDown = new Set([ScreenSize.XSmall, ScreenSize.Small, ScreenSize.Medium])
const MediumUp = new Set([ScreenSize.Medium, ScreenSize.Large, ScreenSize.XLarge])
const LargeUp = new Set([ScreenSize.Large, ScreenSize.XLarge])

@Directive({
  selector: '[ifScreenSize]',
  standalone: true,
})
export class ScreenSizeDirective implements OnInit, OnDestroy {
  @Input()
  ifScreenSize: SizeTarget

  readonly finalize = new Subject<void>()

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private screenSizeService: ScreenSizeService,
  ) {}

  ngOnInit(): void {
    this.screenSizeService.size
      .pipe(filter(isNotNullOrUndefined), distinctUntilChanged(), takeUntil(this.finalize))
      .subscribe(current => this.updateView(current))
  }

  ngOnDestroy(): void {
    this.finalize.next()
    this.finalize.complete()
  }

  updateView(current: ScreenSize): void {
    this.viewContainer.clear()
    if (this.isCurrentlyWithinScreenSize(current)) {
      this.viewContainer.createEmbeddedView(this.templateRef)
    }
  }

  isCurrentlyWithinScreenSize(current: ScreenSize): boolean {
    switch (this.ifScreenSize) {
      case 'xsmall-down':
        return current === ScreenSize.XSmall
      case 'small-down':
        return SmallDown.has(current)
      case 'medium':
        return current === ScreenSize.Medium
      case 'medium-down':
        return MediumDown.has(current)
      case 'medium-up':
        return MediumUp.has(current)
      case 'large':
        return current === ScreenSize.Large
      case 'large-up':
        return LargeUp.has(current)
      case 'xlarge':
        return current === ScreenSize.XLarge
      default:
        assertUnreachable(this.ifScreenSize)
    }
  }
}
